import React, { useCallback, useEffect, useState } from "react";
import {
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import AccordionBox from "components/Layouts/AccordionBox";
import ColourConstants from "helpers/colourConstants";
import { AddNoteDialog, NoteRow } from "components/Modules/CommonNotes";
import { addDefectNotes, getDefectNotes, updateDefectNote } from "services/defects/details";
import { useDispatch } from "react-redux";
import { showError } from "../../../../redux/common/actions";
import DeleteDialog from "components/Elements/DeleteDialog";
import { Apis } from "services/api";
import NoteContentPopup from "components/Elements/NoteContentPopup";

const useStyles = makeStyles()((theme) => ({
	noteContainer: {
		// marginTop: 25,
		display: "flex",
		justifyContent: "flex-end",
	},
	expandIcon: {
		transform: "scale(0.8)",
	},
	sectionHeading: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "17px",
	},
	noteAccordion: {
		borderColor: ColourConstants.commonBorder,
		borderStyle: "solid",
		borderWidth: 1,
		width: "100%",
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
	},

	deleteIcon: {
		transform: "scale(0.7)",
		color: ColourConstants.deleteButton,
		"&:hover": {
			cursor: "pointer",
		},
	},
	view: {
		textDecoration: "underline",
		color: "#307ad7",
		cursor: "pointer",
	},

	actionButton: { padding: "0px 13px 12px 6px" },
}));

const Notes = ({ data, defectId, isReadOnly }) => {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const [defect, setDefect] = useState({
		openAddModal: false,
		openDeleteModal: false,
		openContentModal: false,
	});
	const [notes, setNotes] = useState([]);
	const [editData, setEditData] = useState(null);
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);

	const handleAddNote = async (note) => {
		const response = await addDefectNotes({ defectID: defectId, note });

		if (response.status) {
			fetchNotes();
			return { success: true };
		} else {
			dispatch(
				showError(
					response?.data?.error ||
						t("validation.couldNotAddEntity", { entity: t("label.note") })
				)
			);
			return { success: false };
		}
	};

	const handleUpdateNote = async (noteId, note) => {
		const response = await updateDefectNote(noteId, note);

		if (response.status) {
			fetchNotes();
			return { success: true };
		} else {
			dispatch(
				showError(
					response?.data?.error ||
						t("validation.couldNotUpdateEntity", { entity: t("label.note") })
				)
			);
			return { success: false };
		}
	};

	const handleEditNote = (noteData) => {
		setEditData(noteData);
		setDefect((prev) => ({ ...prev, openAddModal: true }));
	};

	const handleDelete = async () => {
		fetchNotes();
		onCloseDeleteDialog();
	};

	const fetchNotes = useCallback(async () => {
		const response = await getDefectNotes(defectId);

		if (response.status) {
			setNotes(response.data);
		} else {
			dispatch(
				showError(
					response?.data?.error ||
						t("validation.couldNotFetchEntity", { entity: t("label.note") })
				)
			);
		}
		setLoading(false);
	}, [defectId, dispatch, t]);

	const onOpenDeleteDialog = (id) =>
		setDefect({ ...defect, openDeleteModal: { id, open: true } });

	const onCloseDeleteDialog = () =>
		setDefect({ ...defect, openDeleteModal: false });

	const onOpenAddDialog = () =>
		setDefect((prev) => ({ ...prev, openAddModal: true }));

	const onCloseAddDialog = () => {
		setEditData(null);
		setDefect((prev) => ({ ...prev, openAddModal: false }));
	};

	const onCloseContentDialog = () =>
		setDefect({ ...defect, openContentModal: false });
	const onOpenContentDialog = (note) =>
		setDefect({ ...defect, openContentModal: { open: true, note } });

	useEffect(() => {
		fetchNotes();
	}, [fetchNotes]);

	return (
		<div className={classes.noteContainer}>
			<NoteContentPopup
				open={defect.openContentModal.open ?? false}
				onClose={onCloseContentDialog}
				note={defect.openContentModal.note}
			/>
			<AddNoteDialog
				open={defect.openAddModal}
				handleClose={onCloseAddDialog}
				createHandler={handleAddNote}
				updateHandler={handleUpdateNote}
				editData={editData}
			/>
			<DeleteDialog
				entityName={t("label.note")}
				open={defect?.openDeleteModal?.open ?? false}
				closeHandler={onCloseDeleteDialog}
				deleteEndpoint={Apis.DefectNotes}
				deleteID={defect?.openDeleteModal?.id}
				handleRemoveData={handleDelete}
				getError={(error) =>
					dispatch(
						showError(
							error ||
								t("validation.couldNotDeleteEntity", { entity: t("label.note") })
						)
					)
				}
			/>

			<AccordionBox
				title={`${t("title.notes")} (${notes?.length})`}
				isActionsPresent={isReadOnly ? false : true}
				buttonName={t("action.addNote")}
				buttonAction={onOpenAddDialog}
				accordianDetailsCss="table-container"
			>
				{loading ? (
					<CircularProgress />
				) : (
					<Table>
						<TableHead className={classes.tableHead}>
							<TableRow>
								<TableCell style={{ width: "170px" }}>
									{t("label.name")}
								</TableCell>
								<TableCell>{t("label.date")}</TableCell>
								<TableCell>{t("label.note")}</TableCell>
								<TableCell></TableCell>
								<TableCell></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{notes?.map((row) => (
								<NoteRow
									key={row.id}
									row={row}
									classes={classes}
									onDeleteNote={onOpenDeleteDialog}
									onEditNote={handleEditNote}
									onViewNote={onOpenContentDialog}
									isReadOnly={isReadOnly}
								/>
							))}
						</TableBody>
					</Table>
				)}
			</AccordionBox>
		</div>
	);
};

export default Notes;
